import React, { ReactFragment, RefObject } from 'react';
import classNames from 'classnames';

import { ReactComponent as CloseIcon } from 'src/assets/svgs/icon-close.svg';

export enum PopoverPosition {
  Bottom = 'bottom',
  Right = 'right',
}

export interface PopoverSection {
  sectionTitle?: string;
  sectionBody: string | ReactFragment;
}

interface Props {
  dataTestID?: string;
  position?: PopoverPosition;
  popoverSubtitle?: string;
  popoverTitle: string;
  popoverRef?: RefObject<HTMLDivElement>;
  sections: PopoverSection[];
  visible?: boolean;
  onClose?: () => void;
}

export enum PopoverTestIDs {
  PopoverTitle = 'PopoverTitle',
  SectionTitle = 'SectionTitle',
  SectionBody = 'SectionBody',
  CloseButton = 'CloseButton',
  Container = 'Container',
}

const getPopoverClassName = ({ position, visible }) =>
  classNames(
    'absolute',
    'shadow-lg',
    'rounded',
    'bg-white',
    'w-100',
    'border',
    'border-gray-350',
    'transition-opacity',
    'duration-300',
    {
      'block opacity-100 z-20': visible,
      'hidden opacity-0 -z-1': !visible,
    },
    {
      'mt-5 -ml-50 before:tail-top after:tail-top-inset': position === PopoverPosition.Bottom,
      'ml-10 before:tail-left after:tail-left-inset': position === PopoverPosition.Right,
    },
  );

const getPopoverStyle = (position) => ({
  // This should do width/height calculation in pixels, but 47% is good enough for now to unblock.
  transform: position === PopoverPosition.Right ? 'translate(0, -47%)' : '',
});

export const Popover: React.FC<Props> = ({
  dataTestID,
  position = PopoverPosition.Bottom,
  popoverSubtitle,
  popoverTitle,
  popoverRef,
  sections,
  visible = false,
  onClose,
}) => {
  return (
    <div data-testid={dataTestID || PopoverTestIDs.Container} ref={popoverRef}>
      <div className={getPopoverClassName({ position, visible })} style={getPopoverStyle(position)}>
        <div className="relative pt-8 pb-2 px-8" data-testid={PopoverTestIDs.PopoverTitle}>
          <button
            className="absolute p-2 m-3 bg-gray-200 rounded-full top-0 right-0 z-10"
            data-testid={PopoverTestIDs.CloseButton}
            onClick={onClose}
          >
            <CloseIcon />
          </button>

          <div className="text-base text-black font-bold text-2xl normal-case">{popoverTitle}</div>

          {popoverSubtitle && (
            <div className="text-base text-gray-700 font-light normal-case mt-2">
              {popoverSubtitle}
            </div>
          )}
        </div>
        <ul>
          {sections.map((section, index) => (
            <li className="last:pb-8" key={`${popoverTitle}.${index}`}>
              {section.sectionTitle && (
                <div
                  className="text-left text-black text-sm font-extrabold normal-case px-8 pt-4 whitespace-normal"
                  data-testid={PopoverTestIDs.SectionTitle}
                >
                  {section.sectionTitle}
                </div>
              )}

              <div
                className="text-left text-black text-sm font-normal normal-case px-8 pt-4 leading-4 whitespace-normal"
                data-testid={PopoverTestIDs.SectionBody}
              >
                {section.sectionBody}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
